import { commonAPI } from '@/services-ts/axios'
import type {
  IDeleteChallengeTask,
  IGetChallengeList,
  IGetChallengeTaskList,
  IGetChallengeTaskReport,
  IGetChallengeUserReport,
  IPayloadChallenge,
  IPayloadChallengeTask,
  IReorderChallengeTask,
} from '@/types/Request'
import type { IChallengeStore } from '@/types/Store/State'
import { Alert, Format } from '@/utils-ts'
import { defineStore } from 'pinia'

import { useFilterStore } from '../'

export const useChallengeStore = defineStore('challenge', {
  state: (): IChallengeStore => ({
    challenges: {
      data: [],
      total: 0,
    },
    tasks: {
      data: [],
      total: 0,
    },
    userReports: {
      data: [],
      total: 0,
    },
    taskReports: [],
  }),

  actions: {
    async GetChallengeTaskReport(payload: IGetChallengeTaskReport) {
      const { status, data } = await commonAPI.ChallengeServices.GetChallengeTaskReport(payload)
      this.taskReports = status ? data || [] : []
    },

    async GetChallengeUserReport(payload: IGetChallengeUserReport) {
      const { status, data, total } =
        await commonAPI.ChallengeServices.GetChallengeUserReport(payload)
      this.userReports = status ? { total: total || 0, data: data || [] } : { total: 0, data: [] }
    },
    async GetChallengeTaskReportExcel(payload: IGetChallengeTaskReport) {
      await commonAPI.ChallengeServices.GetChallengeTaskReportExcel(payload)
    },

    async GetChallengeUserReportExcel(payload: IGetChallengeUserReport) {
      await commonAPI.ChallengeServices.GetChallengeUserReportExcel(payload)
    },

    async GetChallengeList(payload: IGetChallengeList) {
      const { status, data, total } = await commonAPI.ChallengeServices.GetChallengeList(payload)
      this.challenges = status
        ? {
            total: total || 0,
            data:
              data.map(challenge => {
                const filter = challenge.filterId
                  ? useFilterStore().GetFilterByIdSync(challenge.filterId)
                  : null
                return { ...challenge, filterName: filter ? filter.name : null }
              }) || [],
          }
        : { total: 0, data: [] }
    },

    async CreateChallenge(payload: IPayloadChallenge) {
      const { status, data } = await commonAPI.ChallengeServices.CreateChallenge(payload)
      if (status && data) {
        const filter = payload.filterId
          ? useFilterStore().GetFilterByIdSync(payload.filterId)
          : null

        this.challenges.data.push({
          id: data,
          ...payload,
          imageUrl: payload.image ? URL.createObjectURL(payload.image) : '',
          isActive: false,
          filterName: filter ? filter.name : null,
        })
        this.challenges.total += 1
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async UpdateChallenge(payload: IPayloadChallenge) {
      const { status, data } = await commonAPI.ChallengeServices.UpdateChallenge(payload)
      if (status && data) {
        const index = this.challenges.data.findIndex(challenge => challenge.id === data.id)
        const item = this.challenges.data[index]

        const filter = payload.filterId
          ? useFilterStore().filterList.data.find(f => f.id === payload.filterId)
          : null

        if (index !== -1 && item) {
          this.challenges.data[index] = {
            ...this.challenges.data[index],
            ...data,
            imageUrl: payload.image ? URL.createObjectURL(payload.image) : null,
            filterName: filter ? filter.name : null,
          }
          void Alert({ message: Format('admin_global_success'), type: 'success' })
        }
      }
      return status
    },

    async DeleteChallenge(Id: string) {
      const { status } = await commonAPI.ChallengeServices.DeleteChallenge({ id: Id })
      if (status) {
        this.challenges.data = this.challenges.data.filter(item => item.id !== Id)
        this.challenges.total -= 1
        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
      }
      return status
    },

    /* Challenge Tasks */
    async GetChallengeTaskList(payload: IGetChallengeTaskList) {
      const response = await commonAPI.ChallengeServices.GetChallengeTaskList(payload)
      const { status, data, total } = response

      if (status) {
        // If data itself contains the list and total (sometimes happens with backend wrappers)
        const items = Array.isArray(data) ? data : (data as any)?.data || []
        const count = typeof total === 'number' ? total : (data as any)?.total || 0

        this.tasks = {
          total: count,
          data: items,
        }
      } else {
        this.tasks = { total: 0, data: [] }
      }
    },

    async CreateChallengeTask(payload: IPayloadChallengeTask) {
      const { status, data } = await commonAPI.ChallengeServices.CreateChallengeTask(payload)
      if (status && data) {
        this.tasks.data.push({
          id: data,
          ...payload,
          order: this.tasks.data.length + 1,
          targetEntityId: payload.targetEntityId || '',
        })
        this.tasks.total += 1
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async UpdateChallengeTask(payload: IPayloadChallengeTask) {
      const { status, data } = await commonAPI.ChallengeServices.UpdateChallengeTask(payload)
      if (status && data) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    async DeleteChallengeTask(payload: IDeleteChallengeTask) {
      const { status } = await commonAPI.ChallengeServices.DeleteChallengeTask(payload)
      if (status) {
        this.tasks.data = this.tasks.data.filter(item => item.id !== payload.id)
        this.tasks.total -= 1
        void Alert({ message: Format('admin_global_deleted'), type: 'success' })
      }
      return status
    },

    async ReorderChallengeTask(payload: IReorderChallengeTask) {
      const { status } = await commonAPI.ChallengeServices.ReorderChallengeTask(payload)
      if (status) {
        void Alert({ message: Format('admin_global_success'), type: 'success' })
      }
      return status
    },

    ClearReports() {
      this.userReports = {
        data: [],
        total: 0,
      }
      this.taskReports = []
    },

    $RESET() {
      this.challenges = {
        data: [],
        total: 0,
      }
      this.tasks = {
        data: [],
        total: 0,
      }
      this.ClearReports()
    },
  },
})
